'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface FooterProps {
  className?: string;
}

const Footer = ({ className = '' }: FooterProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState(2026);

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear());
  }, []);

  const footerLinks = {
    platform: [
      { label: 'Live PNR Dashboard', href: '/live-pnr-dashboard' },
      { label: 'Itinerary Gallery', href: '/itinerary-gallery' },
      { label: 'Booking Management', href: '/booking-management' },
      { label: 'Partner Integration', href: '/partner-integration' },
    ],
    company: [
      { label: 'Trust Center', href: '/trust-center' },
      { label: 'About Us', href: '/homepage#about' },
      { label: 'Careers', href: '/homepage#careers' },
      { label: 'Press Kit', href: '/homepage#press' },
    ],
    support: [
      { label: 'Help Center', href: '/homepage#help' },
      { label: 'API Documentation', href: '/partner-integration#api' },
      { label: 'Contact Support', href: '/contact-support' },
      { label: 'System Status', href: '/live-pnr-dashboard#status' },
    ],
    legal: [
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Terms & Conditions', href: '/terms-and-conditions' },
      { label: 'Cookie Policy', href: '/terms-and-conditions#cookies' },
      { label: 'GDPR Compliance', href: '/trust-center#gdpr' },
    ],
  };

  const socialLinks = [
    { name: 'Twitter', icon: 'AtSymbolIcon', href: '#' },
    { name: 'LinkedIn', icon: 'BuildingOfficeIcon', href: '#' },
    { name: 'Facebook', icon: 'UserGroupIcon', href: '#' },
    { name: 'Instagram', icon: 'CameraIcon', href: '#' },
  ];

  if (!isHydrated) {
    return (
      <footer className={`bg-charcoal text-white ${className}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <p className="text-sm text-slate">
              © 2026 Dialforflight. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className={`bg-charcoal text-white ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link 
              href="/homepage" 
              className="flex items-center gap-3 group"
            >
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L20 8V16L12 22L4 16V8L12 2Z" fill="#FF6B35" />
                    <path d="M12 7L16 10V14L12 17L8 14V10L12 7Z" fill="white" />
                  </svg>
                </div>
              </div>
              <div className="flex flex-col min-w-0">
                <span className="font-heading font-black text-sm sm:text-base md:text-lg text-primary leading-tight group-hover:text-secondary transition-colors break-words max-w-[140px] sm:max-w-none">
                  DIAL FOR FLIGHT
                </span>
                <span className="text-[10px] sm:text-xs text-muted-foreground font-bold leading-none mt-1 tracking-wide whitespace-nowrap">
                  ✈️ AI-Powered Travel
                </span>
              </div>
            </Link>
            <p className="text-sm text-slate mb-6 max-w-xs">
              AI-powered travel platform for UK customers. Innovative automation, smart recommendations, and transparent GBP pricing for seamless travel planning.
            </p>
            
            {/* Social Links */}
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-accent transition-colors duration-300"
                  aria-label={social.name}
                >
                  <Icon name={social.icon as any} size={20} />
                </a>
              ))}
            </div>
          </div>

          {/* Platform Links */}
          <div>
            <h3 className="font-heading font-semibold text-white mb-4">Platform</h3>
            <ul className="space-y-3">
              {footerLinks.platform.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-slate hover:text-accent transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="font-heading font-semibold text-white mb-4">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-slate hover:text-accent transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h3 className="font-heading font-semibold text-white mb-4">Support</h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-slate hover:text-accent transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="font-heading font-semibold text-white mb-4">Legal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-slate hover:text-accent transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* UK Office Map Section */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Office Information */}
            <div>
              <h3 className="font-heading font-bold text-xl text-white mb-4 flex items-center space-x-2">
                <Icon name="MapPinIcon" size={24} className="text-primary" />
                <span>UK Office Location</span>
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Icon name="BuildingOfficeIcon" size={20} className="text-primary mt-1" />
                  <div>
                    <p className="text-white font-semibold mb-1">DIAL FOR FLIGHT</p>
                    <p className="text-slate text-sm">Private Limited Company</p>
                    <p className="text-slate text-sm">Company Number: 17005493</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="MapIcon" size={20} className="text-primary mt-1" />
                  <div>
                    <p className="text-white font-semibold mb-1">Registered Address</p>
                    <p className="text-slate text-sm">36 Santiago Street</p>
                    <p className="text-slate text-sm">Manchester, M14 4BL</p>
                    <p className="text-slate text-sm">United Kingdom</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="PhoneIcon" size={20} className="text-primary mt-1" />
                  <div>
                    <p className="text-white font-semibold mb-1">Contact Number</p>
                    <a href="tel:02046152876" className="text-accent hover:text-secondary transition-colors text-sm font-bold">
                      020 4615 2876
                    </a>
                    <br />
                    <a
                      href="tel:02046152876"
                      className="inline-flex items-center gap-1 mt-2 px-3 py-1.5 bg-trust-green text-white text-xs font-bold rounded-lg hover:bg-emerald-600 transition-colors"
                    >
                      <Icon name="PhoneIcon" size={14} className="text-white" />
                      Call Now
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="EnvelopeIcon" size={20} className="text-primary mt-1" />
                  <div>
                    <p className="text-white font-semibold mb-1">Email</p>
                    <a href="mailto:info@dialforflight.co.uk" className="text-accent hover:text-secondary transition-colors text-sm">
                      info@dialforflight.co.uk
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Google Map */}
            <div className="rounded-lg overflow-hidden shadow-brand-lg h-80 lg:h-auto">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2375.0234567890123!2d-2.2166!3d53.4567!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTPCsDI3JzI0LjEiTiAywrAxMycwMC4wIlc!5e0!3m2!1sen!2suk!4v1234567890123!5m2!1sen!2suk&q=36+Santiago+Street,+Manchester,+M14+4BL,+UK"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="DIAL FOR FLIGHT UK Office Location"
              ></iframe>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-sm text-slate mb-1">
                © {currentYear} DIAL FOR FLIGHT - Private Limited Company. All rights reserved.
              </p>
              <p className="text-xs text-slate/80">
                Company Number: 17005493 | Registered Address: 36 Santiago Street, Manchester, M14 4BL, UK
              </p>
              <p className="text-xs text-slate/80 mt-1">
                Contact: 020 4615 2876 | info@dialforflight.co.uk
              </p>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Icon name="ShieldCheckIcon" size={16} className="text-trust-green" />
                <span className="text-xs text-slate">PCI DSS Compliant</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="LockClosedIcon" size={16} className="text-sky-blue" />
                <span className="text-xs text-slate">SSL Encrypted</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;